import React, { useState, useEffect, useMemo } from 'react';
import { Box, Typography, Avatar, Tabs, Tab, Button, TextareaAutosize, IconButton, Dialog, Tooltip, Breadcrumbs, Link } from '@mui/material';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import BoltIcon from '@mui/icons-material/Bolt';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import VerifiedIcon from '@mui/icons-material/Verified';
import CloseIcon from '@mui/icons-material/Close';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import QuizResultsTab from './QuizResultsTab';
import TechnicalConceptsTab from './TechnicalConceptsTab';
import CodingQuestionTab from './CodingQuestionTab';
import { getSubmissionDetails, saveMentorFeedback, verifyMentorReviews, getTlcScores } from '../../../../services/mentorServices/ReviewServices';
import { useFilters } from '../../../../context/FilterContext';
import { toast } from 'react-toastify';
import './SubmissionDetails.css';

const SubmissionDetails = ({ isAdmin = false }) => {
    const navigate = useNavigate();
    const { submissionId, coeName } = useParams();
    const location = useLocation();
    const learnerId = location.state?.learnerId;
    const { reportViewerReviewState, setReportViewerReviewState } = useFilters();

    const user = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem('user')) || {};
        } catch {
            return {};
        }
    }, []);

    const userRole = user?.role || '';
    const isReportViewer = Boolean(
        coeName ||
        userRole === 'coe_report_viewer' ||
        location.state?.fromReportViewer ||
        (location.pathname.includes('/learner-reviews/') && !location.pathname.startsWith('/mentor') && !location.pathname.startsWith('/admin'))
    );

    const getBaseReviewPath = () => {
        if (coeName) return `/${coeName}/learner-reviews`;
        const user = JSON.parse(localStorage.getItem('user')) || {};
        if (user?.role === 'coe_report_viewer') {
            const formattedCoeName = user?.coeName ? user.coeName.trim().replace(/\s+/g, '-') : (user?.coe_name ? user.coe_name.trim().replace(/\s+/g, '-') : 'coe');
            return `/${formattedCoeName}/learner-reviews`;
        }
        return isAdmin ? '/admin/learner-reviews' : '/mentor/learner-reviews';
    };

    const [activeTab, setActiveTab] = useState(0);
    const [showWarning, setShowWarning] = useState(true);
    const [openVerifyModal, setOpenVerifyModal] = useState(false);
    const [openSaveModal, setOpenSaveModal] = useState(false);
    const [evaluationJustifications, setEvaluationJustifications] = useState({});

    const [submissionData, setSubmissionData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [feedbackState, setFeedbackState] = useState({});
    const [saving, setSaving] = useState(false);
    const [verifying, setVerifying] = useState(false);
    const [tlcScores, setTlcScores] = useState(null);

    useEffect(() => {
        window.scrollTo(0, 0);
        if (learnerId && submissionId) {
            fetchSubmissionDetails();
        }
    }, [learnerId, submissionId]);

    const fetchSubmissionDetails = async () => {
        setLoading(true);
        try {
            const res = await getSubmissionDetails(learnerId, submissionId);
            const responseData = res?.data;
            if (responseData?.success) {
                setSubmissionData(responseData.data);
                const reviewId = responseData.data?.review?.reviewId;
                if (reviewId) {
                    try {
                        const tlcRes = await getTlcScores(reviewId, learnerId);
                        if (tlcRes?.data?.success) {
                            setTlcScores(tlcRes.data.data);
                        } else {
                            toast.error(tlcRes?.data?.message || "Failed to fetch TLC scores.");
                        }
                    } catch (err) {
                        console.error("Failed to fetch TLC scores:", err);
                        toast.error(err?.response?.data?.message || "Error fetching TLC scores.");
                    }
                }
            } else {
                toast.error(responseData?.message || "Failed to fetch submission details.");
            }
        } catch (error) {
            console.error("Failed to fetch submission details:", error);
            toast.error(error?.response?.data?.message || "Error fetching submission details.");
        } finally {
            setLoading(false);
        }
    };

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    const handleFeedbackChange = (evaluationId, score, comment) => {
        if (!evaluationId) return;
        const details = getEvaluationDetails(evaluationId);
        const origFeedback = details.originalFeedback || "";
        const origScore = details.originalScore;

        setFeedbackState(prev => {
            const current = prev[evaluationId] || {};
            const newScore = score !== undefined ? score : current.score;
            const newFeedback = comment !== undefined ? comment : (current.feedback !== undefined ? current.feedback : origFeedback);

            const isScoreChanged = newScore !== undefined && newScore !== origScore;
            const isFeedbackChanged = newFeedback !== origFeedback;

            if (!isScoreChanged && !isFeedbackChanged) {
                const updated = { ...prev };
                delete updated[evaluationId];
                return updated;
            }

            return {
                ...prev,
                [evaluationId]: {
                    evaluationId,
                    score: newScore,
                    feedback: newFeedback
                }
            };
        });
    };

    const getEvaluationDetails = (evalId) => {
        if (!submissionData?.evaluations) {
            return {
                evalId,
                section: 'unknown',
                label: 'Justification',
                placeholder: 'feedback',
                existingJustification: '',
                originalFeedback: '',
                originalScore: undefined
            };
        }
        if (submissionData.evaluations.quiz?.evaluationId === evalId) {
            const ev = submissionData.evaluations.quiz;
            return {
                evalId,
                section: 'quiz',
                label: 'Quiz Justification',
                placeholder: 'quiz feedback',
                existingJustification: ev.mentorEvaluation?.justification || '',
                originalFeedback: ev.mentorEvaluation?.feedback || '',
                originalScore: ev.mentorEvaluation?.score !== undefined ? ev.mentorEvaluation.score : ev.score
            };
        }
        if (submissionData.evaluations.technical?.evaluations?.length) {
            const idx = submissionData.evaluations.technical.evaluations.findIndex(e => e.evaluationId === evalId);
            if (idx !== -1) {
                const ev = submissionData.evaluations.technical.evaluations[idx];
                return {
                    evalId,
                    section: 'technical',
                    label: `Concept ${idx + 1} Justification`,
                    placeholder: `concept ${idx + 1}`,
                    existingJustification: ev.mentorEvaluation?.justification || '',
                    originalFeedback: ev.mentorEvaluation?.feedback || '',
                    originalScore: ev.mentorEvaluation?.score !== undefined ? ev.mentorEvaluation.score : ev.score
                };
            }
        }
        if (submissionData.evaluations.coding?.evaluations?.length) {
            const idx = submissionData.evaluations.coding.evaluations.findIndex(e => e.evaluationId === evalId);
            if (idx !== -1) {
                const ev = submissionData.evaluations.coding.evaluations[idx];
                return {
                    evalId,
                    section: 'coding',
                    label: `Coding Question ${idx + 1} Justification`,
                    placeholder: `coding question ${idx + 1}`,
                    existingJustification: ev.mentorEvaluation?.justification || '',
                    originalFeedback: ev.mentorEvaluation?.feedback || '',
                    originalScore: ev.mentorEvaluation?.score !== undefined ? ev.mentorEvaluation.score : ev.score
                };
            }
        }
        return {
            evalId,
            section: 'unknown',
            label: 'Justification',
            placeholder: 'feedback',
            existingJustification: '',
            originalFeedback: '',
            originalScore: undefined
        };
    };

    const findJustificationByEvaluationId = (evalId) => {
        if (!submissionData?.evaluations) return "";
        if (submissionData.evaluations.quiz?.evaluationId === evalId) {
            return submissionData.evaluations.quiz.mentorEvaluation?.justification || "";
        }
        if (submissionData.evaluations.technical?.evaluations?.length) {
            const ev = submissionData.evaluations.technical.evaluations.find(e => e.evaluationId === evalId);
            if (ev) return ev.mentorEvaluation?.justification || "";
        }
        if (submissionData.evaluations.coding?.evaluations?.length) {
            const ev = submissionData.evaluations.coding.evaluations.find(e => e.evaluationId === evalId);
            if (ev) return ev.mentorEvaluation?.justification || "";
        }
        return "";
    };

    const handleSaveClick = () => {
        const actualChangedItems = Object.values(feedbackState).filter(item => {
            const details = getEvaluationDetails(item.evaluationId);
            const origFeedback = details.originalFeedback || "";
            const origScore = details.originalScore;
            const isScoreChanged = item.score !== undefined && item.score !== origScore;
            const isFeedbackChanged = (item.feedback !== undefined) && ((item.feedback || "") !== origFeedback);
            return isScoreChanged || isFeedbackChanged;
        });

        if (actualChangedItems.length === 0) {
            setFeedbackState({});
            toast.info("No changes to save.");
            return;
        }

        if (actualChangedItems.length !== Object.keys(feedbackState).length) {
            const cleaned = {};
            actualChangedItems.forEach(item => {
                cleaned[item.evaluationId] = item;
            });
            setFeedbackState(cleaned);
        }

        const initialJustifications = {};
        actualChangedItems.forEach(item => {
            const details = getEvaluationDetails(item.evaluationId);
            initialJustifications[item.evaluationId] = details.existingJustification || "";
        });

        setEvaluationJustifications(initialJustifications);
        setOpenSaveModal(true);
    };

    const handleSave = async (isVerifyFlow = false) => {
        const rawFeedbacks = Object.values(feedbackState).filter(item => {
            const details = getEvaluationDetails(item.evaluationId);
            const origFeedback = details.originalFeedback || "";
            const origScore = details.originalScore;
            const isScoreChanged = item.score !== undefined && item.score !== origScore;
            const isFeedbackChanged = (item.feedback !== undefined) && ((item.feedback || "") !== origFeedback);
            return isScoreChanged || isFeedbackChanged;
        });
        
        if (rawFeedbacks.length === 0) {
            if (!isVerifyFlow) toast.info("No changes to save.");
            return true;
        }

        if (!isVerifyFlow) {
            const missingItem = rawFeedbacks.find(item => !(evaluationJustifications[item.evaluationId] || "").trim());
            if (missingItem) {
                const details = getEvaluationDetails(missingItem.evaluationId);
                toast.error(`Justification is required for ${details.label.replace(' Justification', '')}.`);
                return false;
            }
        }

        setSaving(true);
        try {
            const questionFeedbacks = rawFeedbacks.map(item => {
                const itemJustif = (evaluationJustifications[item.evaluationId] || "").trim() || findJustificationByEvaluationId(item.evaluationId);

                return {
                    evaluationId: item.evaluationId,
                    ...(item.score !== undefined && { score: item.score }),
                    ...(item.feedback !== undefined && { feedback: item.feedback }),
                    ...(!isVerifyFlow && itemJustif && { justification: itemJustif })
                };
            });

            const payload = { questionFeedbacks };
            const res = await saveMentorFeedback(learnerId, submissionId, payload);
            if (res?.data?.success) {
                if (!isVerifyFlow) {
                    toast.success("Feedback saved successfully.");
                    fetchSubmissionDetails();
                    setOpenSaveModal(false);
                }
                setFeedbackState({}); // Clear after saving
                setEvaluationJustifications({});
                return true;
            } else {
                toast.error(res?.data?.message || "Failed to save feedback.");
                return false;
            }
        } catch (error) {
            console.error("Save error:", error);
            toast.error(error?.response?.data?.message || "Error saving feedback.");
            return false;
        } finally {
            setSaving(false);
        }
    };

    const handleVerify = async () => {
        setVerifying(true);
        try {
            const saveSuccess = await handleSave(true);
            if (!saveSuccess) {
                setVerifying(false);
                return;
            }

            const payload = {
                reviews: [
                    {
                        learnerId: learnerId,
                        reviewProgressId: submissionId
                    }
                ]
            };
            const res = await verifyMentorReviews(payload);
            if (res?.data?.success) {
                toast.success("Review marked as verified successfully.");
                setOpenVerifyModal(false);
                fetchSubmissionDetails();
            } else {
                toast.error(res?.data?.message || "Failed to verify review.");
            }
        } catch (error) {
            console.error("Verify error:", error);
            toast.error(error?.response?.data?.message || "Error verifying review.");
        } finally {
            setVerifying(false);
        }
    };

    const selectedLearners = location.state?.selectedLearners || [];
    const currentIndex = selectedLearners.findIndex(l => l.submissionId === submissionId);
    const hasBulkNavigation = selectedLearners.length > 1 && currentIndex !== -1;

    const handleNavigateBulk = (direction) => {
        if (!hasBulkNavigation) return;
        const newIndex = direction === 'prev' ? currentIndex - 1 : currentIndex + 1;
        if (newIndex >= 0 && newIndex < selectedLearners.length) {
            const target = selectedLearners[newIndex];
            navigate(`${getBaseReviewPath()}/${target.submissionId}`, {
                state: {
                    learnerId: target.learnerId,
                    selectedLearners: selectedLearners
                }
            });
        }
    };

    const hasDataForActiveTab = () => {
        if (activeTab === 0) return !!submissionData?.evaluations?.quiz;
        if (activeTab === 1) return !!submissionData?.evaluations?.technical?.evaluations?.length;
        if (activeTab === 2) return !!submissionData?.evaluations?.coding?.evaluations?.length;
        return true;
    };

    const isCompleted = submissionData?.progress?.status?.toLowerCase() === 'completed';
    const isVerificationPending = submissionData?.progress?.status === 'VERIFICATION_PENDING';

    const getVerifyTooltipMessage = () => {
        const status = submissionData?.progress?.status;
        if (isCompleted) return "You can not submit this action, review already published";
        if (status === 'LC_PENDING') return "First upload the LC parameters for this learner to perform this action";
        if (status === 'REVIEW_PENDING') return "First submit your feedback and save to perform this action";
        if (status === 'VERIFICATION_PENDING') return "";
        return "Complete pending steps to mark as verified";
    };

    const formatScore = (val) => {
        if (val === undefined || val === null) return "--";
        const num = Number(val);
        return Number.isInteger(num) ? num : Number(num.toFixed(2));
    };

    const resolvedLearnerName = location.state?.learnerName || submissionData?.learner?.name || submissionData?.learnerName || "Learner Details";
    const resolvedReviewName = location.state?.reviewName || reportViewerReviewState?.selectedReview || submissionData?.review?.reviewName || submissionData?.review?.title || '';
    const resolvedLabName = location.state?.labName || reportViewerReviewState?.selectedLab || submissionData?.learner?.labName || '';

    return (
        <Box className="submission-details-container">
            {isReportViewer ? (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, px: 0.5, flexWrap: 'wrap', gap: 1 }}>
                    <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: '14px', '& .MuiBreadcrumbs-separator': { mx: 1 } }}>
                        <Link
                            underline="hover"
                            color="inherit"
                            sx={{ cursor: 'pointer', fontWeight: 500 }}
                            onClick={() => {
                                if (setReportViewerReviewState) {
                                    setReportViewerReviewState(prev => ({
                                        ...prev,
                                        viewMode: 'reviews',
                                        selectedReview: null,
                                        selectedReviewId: null,
                                        selectedLab: null,
                                        selectedLabId: null,
                                        reviewNameFilter: 'all',
                                        reviewNameInput: 'All Reviews',
                                        reviewSearchText: ''
                                    }));
                                }
                                navigate(getBaseReviewPath());
                            }}
                        >
                            Learner Reviews
                        </Link>

                        <Link
                            underline="hover"
                            color="inherit"
                            sx={{ cursor: 'pointer', fontWeight: 500 }}
                            onClick={() => {
                                if (setReportViewerReviewState) {
                                    setReportViewerReviewState(prev => ({
                                        ...prev,
                                        viewMode: 'reviews',
                                        selectedReview: null,
                                        selectedReviewId: null,
                                        selectedLab: null,
                                        selectedLabId: null,
                                        reviewNameFilter: 'all',
                                        reviewNameInput: 'All Reviews',
                                        reviewSearchText: ''
                                    }));
                                }
                                navigate(getBaseReviewPath());
                            }}
                        >
                            Review Breakdown
                        </Link>

                        {resolvedReviewName && (
                            <Link
                                underline="hover"
                                color="inherit"
                                sx={{ cursor: 'pointer', fontWeight: 500 }}
                                onClick={() => {
                                    if (setReportViewerReviewState) {
                                        setReportViewerReviewState(prev => ({
                                            ...prev,
                                            viewMode: 'lab_wise',
                                            selectedLab: null,
                                            selectedLabId: null,
                                            learnerSubmissions: []
                                        }));
                                    }
                                    navigate(getBaseReviewPath());
                                }}
                            >
                                {resolvedReviewName}
                            </Link>
                        )}

                        {resolvedLabName && (
                            <Link
                                underline="hover"
                                color="inherit"
                                sx={{ cursor: 'pointer', fontWeight: 500 }}
                                onClick={() => {
                                    if (setReportViewerReviewState) {
                                        setReportViewerReviewState(prev => ({
                                            ...prev,
                                            viewMode: 'learner_wise'
                                        }));
                                    }
                                    navigate(getBaseReviewPath());
                                }}
                            >
                                {resolvedLabName}
                            </Link>
                        )}

                        <Typography color="text.primary" sx={{ fontWeight: 600 }}>
                            {resolvedLearnerName}
                        </Typography>
                    </Breadcrumbs>

                    <Button
                        startIcon={<ArrowBackIcon />}
                        size="small"
                        variant="outlined"
                        onClick={() => {
                            if (setReportViewerReviewState) {
                                setReportViewerReviewState(prev => ({
                                    ...prev,
                                    viewMode: 'learner_wise'
                                }));
                            }
                            navigate(getBaseReviewPath());
                        }}
                        sx={{ textTransform: 'none', borderRadius: '6px', fontWeight: 600 }}
                    >
                        Back to Submissions
                    </Button>
                </Box>
            ) : (
                <Box className="submission-details-breadcrumb">
                    <span className="breadcrumb-link" onClick={() => navigate(getBaseReviewPath())}>Learner Review</span> &gt; Review
                </Box>
            )}

            <Box className="submission-details-header">
                <Box sx={{ width: '100%' }}>
                    <Box className="submission-details-title-row" sx={{ justifyContent: 'space-between', width: '100%', marginBottom: '8px' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                            <Typography className="submission-details-title">
                                {submissionData?.review?.title || "Loading..."}
                            </Typography>
                            <span className={`status-chip ${submissionData?.progress?.status?.toLowerCase() === 'completed' ? 'reviewed' : 'pending'}`} style={{ padding: '3px 10px', fontSize: '11px', fontWeight: 700, borderRadius: '12px' }}>
                                {submissionData?.progress?.status ? submissionData.progress.status.replace(/_/g, ' ') : "PENDING"}
                            </span>
                        </Box>

                        {hasBulkNavigation && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', color: '#6c757d' }}>
                                <Button
                                    onClick={() => handleNavigateBulk('prev')}
                                    disabled={currentIndex === 0}
                                    disableRipple
                                    sx={{ minWidth: 'auto', padding: 0, color: currentIndex === 0 ? '#ced4da' : '#1976d2', textTransform: 'none', fontSize: '12.5px', fontWeight: 600, '&:hover': { backgroundColor: 'transparent', color: currentIndex === 0 ? '#ced4da' : '#115293' } }}
                                >
                                    &lt; Prev
                                </Button>
                                <Typography sx={{ fontSize: '11px', fontWeight: 600, letterSpacing: '0.5px', color: '#495057' }}>
                                    REVIEW {currentIndex + 1} OF {selectedLearners.length}
                                </Typography>
                                <Button
                                    onClick={() => handleNavigateBulk('next')}
                                    disabled={currentIndex === selectedLearners.length - 1}
                                    disableRipple
                                    sx={{ minWidth: 'auto', padding: 0, color: currentIndex === selectedLearners.length - 1 ? '#ced4da' : '#1976d2', textTransform: 'none', fontSize: '12.5px', fontWeight: 600, '&:hover': { backgroundColor: 'transparent', color: currentIndex === selectedLearners.length - 1 ? '#ced4da' : '#115293' } }}
                                >
                                    Next &gt;
                                </Button>
                            </Box>
                        )}
                    </Box>

                </Box>
            </Box>

            {showWarning && submissionData?.progress?.status?.toLowerCase() !== 'completed' && (
                <Box className="review-warning-banner">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <InfoOutlinedIcon sx={{ color: '#f57c00', fontSize: '20px', flexShrink: 0 }} />
                        <Typography variant="body2" sx={{ color: '#495057', fontSize: '13.5px' }}>
                            This review will only be visible to the learner after you complete the verification of the Quiz, Concepts, Coding Results, and submit the L&amp;C parameters.
                        </Typography>
                    </Box>
                    <IconButton size="small" onClick={() => setShowWarning(false)} sx={{ color: '#6c757d', padding: '4px', flexShrink: 0, '&:hover': { color: '#212529', backgroundColor: 'rgba(0,0,0,0.05)' } }}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>
            )}

            <Box className="submission-top-box">
                <Box className="submission-learner-info" sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <Avatar sx={{ width: 52, height: 52, border: '2px solid #e9ecef', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }} src={submissionData?.learner?.image || "/static/images/avatar/1.jpg"} alt={submissionData?.learner?.name} />
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <Typography variant="h6" sx={{ fontSize: '17px', fontWeight: 700, color: '#212529', lineHeight: 1.2 }}>
                                <span style={{ textTransform: 'capitalize' }}>{submissionData?.learner?.name || "Loading..."}</span>
                                {submissionData?.learner?.rollNo ? <span style={{ fontWeight: 500, fontSize: '13px', color: '#495057' }}>{` - ${submissionData.learner.rollNo}`}</span> : ''}
                            </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                            <Typography variant="caption" sx={{ color: '#495057', fontSize: '13px', fontWeight: 500 }}>
                                {[
                                    submissionData?.learner?.email,
                                    submissionData?.learner?.coeName,
                                    submissionData?.learner?.labName
                                ].filter(Boolean).join(' - ')}
                            </Typography>
                        </Box>

                        {submissionData?.review?.reviewName && (
                            <Box sx={{ display: 'flex', alignItems: 'center', marginTop: '2px' }}>
                                <span style={{ fontSize: '12px', fontWeight: 700, color: '#2e7d32', backgroundColor: '#e8f5e9', padding: '3px 10px', borderRadius: '4px', border: '1px solid #c8e6c9', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                                    <VerifiedIcon sx={{ fontSize: '14px' }} />
                                    REVIEW: {submissionData.review.reviewName}
                                </span>
                            </Box>
                        )}
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: '50px' }}>
                    <Box className="top-box-section" sx={{ borderLeft: '1px solid #e0e0e0', paddingLeft: '50px' }}>
                        <span className="top-box-label">SUBMISSION DATE</span>
                        <span className="top-box-value">
                            {submissionData?.progress?.submittedAt ? new Date(submissionData.progress.submittedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : "--"}
                        </span>
                    </Box>
                    <Box className="top-box-section" sx={{ borderLeft: '1px solid #e0e0e0', paddingLeft: '50px' }}>
                        <span className="top-box-label">CURRENT STATUS</span>
                        <span className="top-box-value" style={{ textTransform: 'uppercase' }}>
                            <span className={`status-dot ${submissionData?.progress?.status?.toLowerCase() === 'completed' ? 'reviewed' : 'pending'}`}></span>
                            {submissionData?.progress?.status ? submissionData.progress.status.replace(/_/g, ' ').toUpperCase() : "PENDING"}
                        </span>
                    </Box>
                </Box>
            </Box>

            <Box className="tech-excellence-section">
                <Box className="tech-excellence-header" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography className="tech-excellence-title" sx={{ color: '#1976d2', fontWeight: 700, }}>TECHABILITY</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', backgroundColor: '#f0f6fc', padding: '6px 12px', borderRadius: '4px', border: '1px solid #b6d4fe' }}>
                        <Typography sx={{ fontSize: '15px', fontWeight: 700, color: '#1976d2' }}>
                            {tlcScores?.techExcellence?.obtained !== undefined ? formatScore(tlcScores.techExcellence.obtained) : "--"}
                            <span style={{ color: '#6c757d', margin: '0 4px' }}>/</span>
                            {tlcScores?.techExcellence?.total !== undefined ? formatScore(tlcScores.techExcellence.total) : "--"}
                        </Typography>
                    </Box>
                </Box>
                <Box className="tech-excellence-cards">
                    <Box className="tech-card">
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                            <span className="tech-card-title" style={{ color: '#1976d2', marginBottom: 0 }}>CONCEPTS (OBJ)</span>
                            <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#6c757d' }}>Max: {tlcScores?.techExcellence?.techConceptsObjective?.max !== undefined ? formatScore(tlcScores.techExcellence.techConceptsObjective.max) : "--"}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            <span className="tech-card-value">
                                {tlcScores?.techExcellence?.techConceptsObjective?.score !== undefined ? formatScore(tlcScores.techExcellence.techConceptsObjective.score) : "--"}
                            </span>
                        </Box>
                    </Box>
                    <Box className="tech-card">
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                            <span className="tech-card-title" style={{ color: '#1976d2', marginBottom: 0 }}>CONCEPTS (SUBJ)</span>
                            <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#6c757d' }}>Max: {tlcScores?.techExcellence?.techConceptsSubjective?.max !== undefined ? formatScore(tlcScores.techExcellence.techConceptsSubjective.max) : "--"}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            <span className="tech-card-value">
                                {tlcScores?.techExcellence?.techConceptsSubjective?.score !== undefined ? formatScore(tlcScores.techExcellence.techConceptsSubjective.score) : "--"}
                            </span>
                        </Box>
                    </Box>
                    <Box className="tech-card">
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                            <span className="tech-card-title" style={{ color: '#1976d2', marginBottom: 0 }}>COMPLETENESS</span>
                            <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#6c757d' }}>Max: {tlcScores?.techExcellence?.solutionCompleteness?.max !== undefined ? formatScore(tlcScores.techExcellence.solutionCompleteness.max) : "--"}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            <span className="tech-card-value">
                                {tlcScores?.techExcellence?.solutionCompleteness?.score !== undefined ? formatScore(tlcScores.techExcellence.solutionCompleteness.score) : "--"}
                            </span>
                        </Box>
                    </Box>
                    <Box className="tech-card">
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                            <span className="tech-card-title" style={{ color: '#1976d2', marginBottom: 0 }}>APPROACH</span>
                            <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#6c757d' }}>Max: {tlcScores?.techExcellence?.problemSolving?.max !== undefined ? formatScore(tlcScores.techExcellence.problemSolving.max) : "--"}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            <span className="tech-card-value">
                                {tlcScores?.techExcellence?.problemSolving?.score !== undefined ? formatScore(tlcScores.techExcellence.problemSolving.score) : "--"}
                            </span>
                        </Box>
                    </Box>
                    <Box className="tech-card">
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                            <span className="tech-card-title" style={{ color: '#1976d2', marginBottom: 0 }}>CODE QUALITY</span>
                            <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#6c757d' }}>Max: {tlcScores?.techExcellence?.codeQuality?.max !== undefined ? formatScore(tlcScores.techExcellence.codeQuality.max) : "--"}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            <span className="tech-card-value">
                                {tlcScores?.techExcellence?.codeQuality?.score !== undefined ? formatScore(tlcScores.techExcellence.codeQuality.score) : "--"}
                            </span>
                        </Box>
                    </Box>
                </Box>
            </Box>

            <Box sx={{ borderBottom: '1px solid #e0e0e0', marginBottom: '20px' }}>
                <Tabs value={activeTab} onChange={handleTabChange}>
                    <Tab label="Quiz Results" sx={{ textTransform: 'none', fontWeight: activeTab === 0 ? 700 : 500 }} />
                    <Tab label="Concept Results" sx={{ textTransform: 'none', fontWeight: activeTab === 1 ? 700 : 500 }} />
                    <Tab label="Coding Results" sx={{ textTransform: 'none', fontWeight: activeTab === 2 ? 700 : 500 }} />
                </Tabs>
            </Box>

            <Box className="main-content-area">
                <Box className="tabs-container">
                    <Box>
                        {activeTab === 0 && <QuizResultsTab data={submissionData?.evaluations?.quiz} feedbackState={feedbackState} onFeedbackChange={handleFeedbackChange} readOnly={isCompleted || isAdmin} isAdmin={isAdmin} />}
                        {activeTab === 1 && <TechnicalConceptsTab data={submissionData?.evaluations?.technical} feedbackState={feedbackState} onFeedbackChange={handleFeedbackChange} readOnly={isCompleted || isAdmin} isAdmin={isAdmin} />}
                        {activeTab === 2 && <CodingQuestionTab data={submissionData?.evaluations?.coding} feedbackState={feedbackState} onFeedbackChange={handleFeedbackChange} readOnly={isCompleted || isAdmin} isAdmin={isAdmin} />}
                    </Box>
                </Box>
            </Box>

            {hasDataForActiveTab() && submissionData?.progress?.status !== 'REVIEW_PENDING' && !isAdmin && (
                <Box className="bottom-action-bar">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#1976d2' }}>
                        <BoltIcon />
                        <Typography variant="body2" sx={{ fontWeight: 700, letterSpacing: '0.5px', color: '#343a40' }}>REVIEW ACTIONS</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: '15px' }}>
                        <Tooltip title={isCompleted ? "You can not submit this action , review already published" : ""} arrow placement="top">
                            <span>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    startIcon={<SaveOutlinedIcon />}
                                    onClick={handleSaveClick}
                                    disabled={saving || isCompleted || Object.keys(feedbackState).length === 0}
                                    sx={{ textTransform: 'none', borderRadius: '8px', padding: '8px 24px', fontWeight: 600, backgroundColor: '#0d6efd', '&:hover': { backgroundColor: '#0b5ed7' }, '&.Mui-disabled': { backgroundColor: '#e9ecef', color: '#adb5bd' } }}
                                >
                                    {saving ? "Saving..." : "Save"}
                                </Button>
                            </span>
                        </Tooltip>
                        <Tooltip title={getVerifyTooltipMessage()} arrow placement="top">
                            <span>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    startIcon={<VerifiedIcon />}
                                    onClick={() => setOpenVerifyModal(true)}
                                    disabled={!isVerificationPending}
                                    sx={{ textTransform: 'none', borderRadius: '8px', padding: '8px 24px', fontWeight: 600, backgroundColor: '#0d6efd', '&:hover': { backgroundColor: '#0b5ed7' }, '&.Mui-disabled': { backgroundColor: '#e9ecef', color: '#adb5bd' } }}
                                >
                                    Mark as Verified
                                </Button>
                            </span>
                        </Tooltip>
                    </Box>
                </Box>
            )}

            {/* Save Feedback Modal */}
            <Dialog 
                open={openSaveModal} 
                onClose={() => setOpenSaveModal(false)} 
                maxWidth="sm" 
                fullWidth 
                PaperProps={{ 
                    sx: { 
                        borderRadius: '12px', 
                        overflow: 'hidden', 
                        boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                        maxHeight: '90vh',
                        display: 'flex',
                        flexDirection: 'column'
                    } 
                }}
            >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 28px', backgroundColor: '#f8f9fa', borderBottom: '1px solid #e9ecef', flexShrink: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <SaveOutlinedIcon sx={{ color: '#1976d2', fontSize: '22px' }} />
                        <Typography sx={{ fontSize: '17px', fontWeight: 700, color: '#212529', letterSpacing: '0.3px' }}>Save Feedback</Typography>
                    </Box>
                    <IconButton size="small" onClick={() => setOpenSaveModal(false)} sx={{ color: '#6c757d', '&:hover': { color: '#212529' } }}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>

                <Box sx={{ 
                    padding: '24px 28px', 
                    overflowY: 'auto', 
                    flex: '1 1 auto',
                    '&::-webkit-scrollbar': {
                        width: '6px',
                    },
                    '&::-webkit-scrollbar-track': {
                        background: '#f8f9fa',
                        borderRadius: '4px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                        background: '#ced4da',
                        borderRadius: '4px',
                    },
                    '&::-webkit-scrollbar-thumb:hover': {
                        background: '#adb5bd',
                    }
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '12px', backgroundColor: '#f0f7ff', border: '1px solid #cce3fd', borderRadius: '8px', padding: '16px', marginBottom: '24px' }}>
                        <InfoOutlinedIcon sx={{ color: '#0056b3', fontSize: '20px', mt: '2px' }} />
                        <Typography sx={{ fontSize: '14.5px', color: '#004085', lineHeight: 1.5, fontWeight: 500 }}>
                            Please provide a justification to change AI feedback.<span style={{ color: '#d32f2f', fontWeight: 'bold' }}> *</span>
                        </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                        {Object.values(feedbackState).map(item => {
                            const details = getEvaluationDetails(item.evaluationId);
                            const hasMultiple = Object.values(feedbackState).length > 1;
                            return (
                                <Box key={item.evaluationId}>
                                    {hasMultiple && (
                                        <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#495057', textTransform: 'uppercase', mb: 1 }}>
                                            {details.label} <span style={{ color: '#d32f2f' }}>*</span>
                                        </Typography>
                                    )}
                                    <Box sx={{
                                        '& textarea': {
                                            width: '100%', 
                                            maxHeight: '25vh', 
                                            overflowY: 'auto', 
                                            border: '1px solid #ced4da', 
                                            borderRadius: '8px', 
                                            padding: '14px 16px', 
                                            fontSize: '14.5px', 
                                            color: '#212529', 
                                            fontFamily: 'inherit', 
                                            resize: 'vertical', 
                                            boxSizing: 'border-box',
                                            outline: 'none',
                                            transition: 'border-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                                            backgroundColor: '#fff'
                                        },
                                        '& textarea:focus': {
                                            borderColor: '#86b7fe',
                                            boxShadow: '0 0 0 4px rgba(13,110,253,.15)',
                                        },
                                        '& textarea::placeholder': {
                                            color: '#adb5bd'
                                        }
                                    }}>
                                        <TextareaAutosize
                                            minRows={hasMultiple ? 2 : 4}
                                            maxRows={6}
                                            value={evaluationJustifications[item.evaluationId] || ""}
                                            onChange={(e) => setEvaluationJustifications(prev => ({ ...prev, [item.evaluationId]: e.target.value }))}
                                            placeholder={`Type your detailed justification for ${details.placeholder} here...`}
                                        />
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '14px', padding: '16px 28px', backgroundColor: '#fff', borderTop: '1px solid #e9ecef', flexShrink: 0 }}>
                    <Button
                        onClick={() => setOpenSaveModal(false)}
                        disableElevation
                        sx={{ textTransform: 'none', fontWeight: 600, color: '#495057', padding: '8px 20px', borderRadius: '8px', border: '1px solid #ced4da', backgroundColor: '#fff', '&:hover': { backgroundColor: '#f8f9fa', borderColor: '#b1bxc4' } }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        disableElevation
                        onClick={() => handleSave(false)}
                        disabled={saving || Object.values(feedbackState).length === 0 || Object.values(feedbackState).some(item => !(evaluationJustifications[item.evaluationId] || "").trim())}
                        sx={{ textTransform: 'none', fontWeight: 600, backgroundColor: '#0d6efd', color: '#fff', padding: '8px 24px', borderRadius: '8px', transition: 'all 0.2s', '&:hover': { backgroundColor: '#0b5ed7', boxShadow: '0 4px 12px rgba(13, 110, 253, 0.2)' }, '&.Mui-disabled': { backgroundColor: '#e9ecef', color: '#adb5bd' } }}
                    >
                        {saving ? "Saving..." : "Confirm Save"}
                    </Button>
                </Box>
            </Dialog>

            {/* Mark as Verified Modal */}
            <Dialog open={openVerifyModal} onClose={() => setOpenVerifyModal(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '8px', overflow: 'hidden' } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 24px', borderBottom: '1px solid #e0e0e0' }}>
                    <Typography sx={{ fontSize: '16px', fontWeight: 700, color: '#212529' }}>Mark as Verified</Typography>
                </Box>

                <Box sx={{ padding: '24px' }}>
                    <Box sx={{ display: 'flex', gap: '12px', backgroundColor: '#fff8eb', border: '1px solid #ffe0b2', borderRadius: '8px', padding: '16px', marginBottom: '20px' }}>
                        <ReportProblemOutlinedIcon sx={{ color: '#f57c00', fontSize: '22px', flexShrink: 0, marginTop: '2px' }} />
                        <Typography sx={{ fontSize: '14px', color: '#495057', lineHeight: 1.5 }}>
                            Please confirm that you have manually verified the Quiz, Concept, and Coding results, and have added and saved your feedback and justification.
                        </Typography>
                    </Box>
                    <Typography sx={{ fontSize: '14px', color: '#495057', lineHeight: 1.6 }}>
                        Once you mark this review as Verified, it will be locked, and you will no longer be able to make any changes to this learner's review.
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', padding: '16px 24px', backgroundColor: '#f8f9fa', borderTop: '1px solid #e0e0e0' }}>
                    <Button
                        onClick={() => setOpenVerifyModal(false)}
                        sx={{ textTransform: 'none', fontWeight: 600, color: '#495057', padding: '6px 16px', borderRadius: '6px', '&:hover': { backgroundColor: 'rgba(0,0,0,0.05)' } }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleVerify}
                        disabled={verifying}
                        sx={{ textTransform: 'none', fontWeight: 600, backgroundColor: '#0056b3', color: '#fff', padding: '6px 20px', borderRadius: '6px', '&:hover': { backgroundColor: '#004085' } }}
                    >
                        {verifying ? "Confirming..." : "Confirm"}
                    </Button>
                </Box>
            </Dialog>
        </Box>
    );
};

export default SubmissionDetails;
